import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Check, ChevronDown, ListOrdered, Plus, Trash2, X } from 'lucide-react';

import { SelectField, INPUT_CLASS, inputStyle } from '@/components/phone/FieldControls';
import {
  BottomSheet, Card, EmptyState, IconButton, PrimaryButton, Spinner, StatusPill,
} from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';
import { DESIGN_STAGE_KEY, clock, time, movementLines } from './processFormat';

/**
 * The job card's process table, on a phone.
 *
 * Thirteen columns across one row is what the card prints; at a handset's width
 * it is a sideways scroll with the remarks four screens from the process they
 * belong to. So each process is a card: the machine and the operator as pickers
 * that open at the thumb, the scanned times and the wastage as facts, and every
 * number the shift may write as a 48px field that saves when it is left. The
 * same locks hold — a finished process is a record, and a row nobody may edit
 * reads as facts only.
 */

const STATE_PILL = {
  running: ['Running', 'accent'],
  completed: ['Done', 'positive'],
  pending: ['Pending', 'neutral'],
};

function Fact({ label, value, testID, children }) {
  return (
    <div className="min-w-0" data-testid={testID}>
      <p className="text-[10px] font-medium uppercase tracking-[0.5px]" style={{ color: Colors.text.secondary }}>
        {label}
      </p>
      <p className="mt-0.5 truncate text-[14px] font-semibold tabular-nums" style={{ color: Colors.text.primary }}>
        {value || '—'}
      </p>
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <p className="mb-1.5 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>{children}</p>
  );
}

/**
 * A machine or a person, picked from a sheet. Linked machines come first under
 * the process's own heading; the clear entry sits at the top so the wrong press
 * comes off as easily as it went on.
 */
function PickField({ row, value, options, labelOf, onPick, onClear, clearLabel, testId, placeholder, title }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const preferred = options.filter(o => o.linked);
  const rest = options.filter(o => !o.linked);
  const groups = preferred.length ? [[`For ${row.process}`, preferred], ['Other', rest]] : [[null, options]];

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(true)}
        className="flex h-12 w-full items-center justify-between gap-2 rounded-[10px] border px-3 text-left"
        style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
      >
        <span className="truncate text-[14px]" style={{ color: value ? Colors.text.primary : Colors.text.tertiary }}>
          {busy ? <Spinner size="sm" /> : (value || placeholder)}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0" style={{ color: Colors.text.secondary }} />
      </button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title={title} subtitle={row.process} testID={`picker-${testId}`}>
        <div className="max-h-[21rem] overflow-y-auto">
          {onClear && (
            <button
              type="button"
              data-testid={`${testId}-clear`}
              onClick={() => run(() => onClear(row))}
              className="flex w-full items-center gap-2 border-b px-1 py-3.5 text-left text-[14px]"
              style={{ borderColor: Colors.border.subtle, color: Colors.text.secondary }}
            >
              <X className="h-4 w-4" /> {clearLabel}
            </button>
          )}
          {options.length === 0 && (
            <p className="py-4 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
              Nothing to pick from yet.
            </p>
          )}
          {groups.map(([heading, list]) => list.length > 0 && (
            <div key={heading || 'all'}>
              {heading && (
                <p className="px-1 pb-1 pt-3 text-[10px] font-medium uppercase tracking-[1px]" style={{ color: Colors.text.tertiary }}>
                  {heading}
                </p>
              )}
              {list.map(option => {
                const on = labelOf(option) === value;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => run(() => onPick(row, option))}
                    className="flex w-full items-center justify-between gap-3 border-b px-1 py-3.5 text-left last:border-b-0"
                    style={{ borderColor: Colors.border.subtle }}
                  >
                    <span
                      className="truncate text-[15px]"
                      style={{ color: on ? Colors.accent.DEFAULT : Colors.text.primary, fontWeight: on ? 700 : 400 }}
                    >
                      {labelOf(option)}
                    </span>
                    {on && <Check className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.accent.DEFAULT }} />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </BottomSheet>
    </>
  );
}

/**
 * A number the shift writes down. It saves when the field is left, and only
 * when it changed — the same rule as the desktop cell, so a tap into a field
 * and out again never writes a zero over a blank.
 */
function WriteField({ row, field, value, onSave, type = 'number', label }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(value == null ? '' : String(value));
  }

  const commit = async () => {
    const next = type === 'number' ? parseInt(draft, 10) : draft;
    const unchanged = type === 'number'
      ? (Number.isNaN(next) ? value == null : next === value)
      : next === (value || '');
    if (unchanged) return;
    await onSave(row, field, type === 'number' ? (Number.isNaN(next) ? 0 : next) : next);
  };

  return (
    <input
      data-testid={`edit-${field}-${row.stage_key}`}
      type={type}
      inputMode={type === 'number' ? 'numeric' : undefined}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      aria-label={`${row.process} ${field}`}
      placeholder={type === 'number' ? '–' : 'Anything worth writing down'}
      className={`${INPUT_CLASS} ${type === 'number' ? 'text-right tabular-nums' : ''}`}
      style={inputStyle}
    />
  );
}

function ProcessCard({
  row, idx, count, locked, canEdit, busy, showFlow, designerOptions,
  onAssignMachine, onAssignOperator, onClearMachine, onEditRow, onMove, onRemove,
}) {
  const isDesign = row.stage_key === DESIGN_STAGE_KEY;
  const [stateLabel, stateTone] = STATE_PILL[row.state] || STATE_PILL.pending;
  const wastage = row.wastage_qty ? `${row.wastage_qty} · ${row.wastage_pct}%` : '';
  const clear = row.machine && onClearMachine ? onClearMachine : undefined;
  const movements = movementLines(row);

  return (
    <Card className="mt-3" testID={`process-row-${row.stage_key}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-[15px] font-bold" style={{ color: Colors.text.primary }}>
          <span className="mr-1.5 tabular-nums" style={{ color: Colors.text.tertiary }}>{idx + 1}</span>
          {row.process}
        </p>
        <StatusPill label={stateLabel} tone={stateTone} dot={row.state === 'running'} />
      </div>

      {locked ? (
        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3">
          <Fact label={isDesign ? 'Designer' : 'Machine'} value={isDesign ? row.operator : row.machine} />
          {!isDesign && <Fact label="Operator" value={row.operator} />}
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <Label>{isDesign ? 'Designer' : 'Machine'}</Label>
            {isDesign ? (
              <PickField
                row={row} value={row.operator} options={designerOptions} labelOf={d => d.name}
                onPick={onAssignOperator} onClear={clear} clearLabel="No machine"
                testId={`designer-cell-${row.stage_key}`} placeholder="Pick designer" title="Who draws it?"
              />
            ) : (
              <PickField
                row={row} value={row.machine} options={row.machine_options || []} labelOf={m => m.machine_name}
                onPick={onAssignMachine} onClear={clear} clearLabel="No machine"
                testId={`machine-cell-${row.stage_key}`} placeholder="Pick" title="Put it on which machine?"
              />
            )}
          </div>
          <div>
            <Label>Operator</Label>
            {isDesign ? (
              <p className="flex h-12 items-center px-1 text-[14px]" style={{ color: Colors.text.tertiary }}>—</p>
            ) : (
              <PickField
                row={row} value={row.operator} options={row.operator_options || []} labelOf={o => o.name}
                onPick={onAssignOperator}
                testId={`operator-cell-${row.stage_key}`} placeholder="Pick" title="Who runs it?"
              />
            )}
          </div>
        </div>
      )}

      {/* the scan's own numbers — never typed, on either screen */}
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
        <Fact label="Start" value={time(row.started_at)} />
        <Fact label="End" value={time(row.ended_at)} />
        <Fact label="Duration" value={clock(row.turnaround_seconds)} />
        <Fact label="Wastage / %" value={wastage} />
        {showFlow && (
          <>
            <Fact label="Received" value={row.qty_received ? row.qty_received.toLocaleString() : ''} testID={`recd-qty-${row.stage_key}`} />
            <Fact
              label="Moved / Short"
              testID={`moved-qty-${row.stage_key}`}
              value={row.qty_moved_on ? (
                <>
                  {row.qty_moved_on.toLocaleString()}
                  {row.qty_short > 0 && <span style={{ color: Colors.status.error }}> · {row.qty_short.toLocaleString()}</span>}
                </>
              ) : ''}
            />
            {movements.length > 0 && (
              <ul className="col-span-2 space-y-1 text-[11px]" style={{ color: Colors.text.secondary }} data-testid={`movements-${row.stage_key}`}>
                {movements.map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            )}
          </>
        )}
      </div>

      {locked ? (
        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
          <Fact label="Setup" value={clock(row.setup_seconds)} />
          <Fact label="Input qty" value={row.input_qty ? row.input_qty.toLocaleString() : ''} />
          <Fact label="Output qty" value={row.output_qty ? row.output_qty.toLocaleString() : ''} />
          <Fact label="Repairs" value={row.repairs || ''} />
          <Fact label="Cartons" value={row.cartons || ''} />
          {!!row.remarks && (
            <div className="col-span-2">
              <Fact label="Remarks" value={row.remarks} />
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
          <div>
            <Label>Setup (min)</Label>
            {row.setup_seconds ? (
              <p className="flex h-12 items-center px-3 text-[14px] font-semibold tabular-nums" style={{ color: Colors.text.primary }}>
                {clock(row.setup_seconds)}
              </p>
            ) : (
              <WriteField row={row} field="setup_seconds" value={null} onSave={(r, f, v) => onEditRow(r, f, v * 60)} />
            )}
          </div>
          <div>
            <Label>Input qty</Label>
            <WriteField row={row} field="input_qty" value={row.input_qty || null} onSave={onEditRow} />
          </div>
          <div>
            <Label>Output qty</Label>
            <WriteField row={row} field="output_qty" value={row.output_qty || null} onSave={onEditRow} />
          </div>
          <div>
            <Label>Repairs</Label>
            <WriteField row={row} field="repairs" value={row.repairs || null} onSave={onEditRow} />
          </div>
          <div>
            <Label>Cartons</Label>
            <WriteField row={row} field="cartons" value={row.cartons || null} onSave={onEditRow} />
          </div>
          <div className="col-span-2">
            <Label>Remarks</Label>
            <WriteField row={row} field="remarks" value={row.remarks} type="text" onSave={onEditRow} />
          </div>
        </div>
      )}

      {canEdit && (
        <div className="mt-3 flex items-center justify-end gap-2 border-t pt-3" style={{ borderColor: Colors.border.subtle }}>
          <IconButton icon={ArrowUp} label={`Move ${row.process} earlier`} disabled={busy || idx === 0} onClick={() => onMove(idx, -1)} />
          <IconButton icon={ArrowDown} label={`Move ${row.process} later`} disabled={busy || idx === count - 1} onClick={() => onMove(idx, 1)} />
          <IconButton icon={Trash2} label={`Remove ${row.process}`} disabled={busy || row.state !== 'pending'} onClick={() => onRemove(row)} />
        </div>
      )}
    </Card>
  );
}

/** Which process, and where it goes — the two answers adding one needs. */
function AddProcessCard({ available = [], rows = [], onAdd, busy }) {
  const [stageKey, setStageKey] = useState('');
  const [afterKey, setAfterKey] = useState('');

  if (!available.length) return null;

  const submit = async () => {
    if (!stageKey) return;
    await onAdd(stageKey, afterKey || null);
    setStageKey('');
    setAfterKey('');
  };

  return (
    <Card className="mt-3" title="Add a process" testID="add-process-card">
      <div className="space-y-3">
        <div>
          <Label>Process</Label>
          <SelectField
            name="add-process" label="Which process?" value={stageKey} placeholder="Pick a process"
            options={available.map(rule => ({ value: rule.stage_key, label: rule.stage_name }))}
            onChange={setStageKey}
          />
        </div>
        <div>
          <Label>After</Label>
          <SelectField
            name="add-process-after" label="Where does it go?" value={afterKey} placeholder="The start of the job"
            options={[
              { value: '', label: 'The start of the job' },
              ...rows.map(row => ({ value: row.stage_key, label: row.process })),
            ]}
            onChange={setAfterKey}
          />
        </div>
        <PrimaryButton onClick={submit} disabled={busy || !stageKey} testID="add-process-btn">
          <Plus className="h-4 w-4" /> Add process
        </PrimaryButton>
        <p className="text-[11px]" style={{ color: Colors.text.tertiary }}>
          Goods already past that point keep going — they skip it.
        </p>
      </div>
    </Card>
  );
}

export default function PhoneProcessCards({
  rows = [], totals = {}, onAssignMachine, onAssignOperator, onClearMachine, onEditRow,
  canEdit = false, onMove, onRemove, onAdd, addableStages = [], busy = false, showFlow = false,
  designerOptions = [],
}) {
  return (
    <div data-testid="process-cards">
      {rows.length === 0 ? (
        <Card className="mt-3">
          <EmptyState icon={ListOrdered} message="No production pipeline on this job card yet." />
        </Card>
      ) : rows.map((row, idx) => (
        <ProcessCard
          key={row.stage_key || row.process}
          row={row} idx={idx} count={rows.length}
          // a finished process is a record of what happened, not a form
          locked={!canEdit || row.state === 'completed'}
          canEdit={canEdit} busy={busy} showFlow={showFlow} designerOptions={designerOptions}
          onAssignMachine={onAssignMachine} onAssignOperator={onAssignOperator}
          onClearMachine={onClearMachine} onEditRow={onEditRow} onMove={onMove} onRemove={onRemove}
        />
      ))}

      {canEdit && <AddProcessCard available={addableStages} rows={rows} onAdd={onAdd} busy={busy} />}

      {/* setup + productive + unproductive = turnaround, the way the card totals it */}
      <Card className="mt-3" title="Time">
        <dl className="text-[13px]">
          {[
            ['Set Up Time', totals.setup_seconds],
            ['Productive Time', totals.productive_seconds],
            ['UnProductive Time', totals.unproductive_seconds],
          ].map(([label, seconds]) => (
            <div key={label} className="flex justify-between border-b py-1.5" style={{ borderColor: Colors.border.subtle }}>
              <dt style={{ color: Colors.text.secondary }}>{label}</dt>
              <dd className="tabular-nums" style={{ color: Colors.text.primary }}>{clock(seconds) || '—'}</dd>
            </div>
          ))}
          <div className="mt-1 flex justify-between border-t-2 pt-2" style={{ borderColor: Colors.accent.DEFAULT }}>
            <dt className="text-[14px] font-bold" style={{ color: Colors.text.primary }}>Turn Around Time</dt>
            <dd className="text-[16px] font-bold tabular-nums" style={{ color: Colors.accent.DEFAULT }} data-testid="turnaround-total">
              {clock(totals.turnaround_seconds) || '—'}
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}
